'use client'

import { useState, useEffect } from 'react'

import Image from 'next/image'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom'

import Breadcrumbs from '@/app/_components/Breadcrumbs'
import FeaturedProductsPopup from '@/app/_components/FeaturedProductsPopup'


/**
 * Client-only random diptych - skips SSR to avoid hydration mismatch
 */
const SingleRandomDiptych = ({ diptychs, category }) => {
    const [diptych, setDiptych] = useState(null)

    useEffect(() => {
        setDiptych(diptychs[Math.floor(Math.random() * diptychs.length)])
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (!diptych) return <div className="w-full h-full bg-gray-2" />

    return (
        <DiptychSlide
            diptych={diptych}
            category={category}
            isFirstSlide={true}
        />
    )
}

/**
 * Individual diptych slide (just the background images)
 */
const DiptychSlide = ({ diptych, category, isFirstSlide, onSlideshowReady }) => {
    const [leftLoaded, setLeftLoaded] = useState(false)
    const [rightLoaded, setRightLoaded] = useState(false)

    // Signal ready when both images loaded (first slide only)
    useEffect(() => {
        if (isFirstSlide && leftLoaded && rightLoaded && onSlideshowReady) {
            onSlideshowReady()
        }
    }, [isFirstSlide, leftLoaded, rightLoaded, onSlideshowReady])

    const leftWidth = diptych.left.proportion === 'three_fifths' ? 'w-3/5' : 'w-2/5'
    const rightWidth = diptych.right.proportion === 'two_fifths' ? 'w-2/5' : 'w-3/5'

    return (
        <div className="flex w-full h-full">
            <div className={`relative ${leftWidth}`}>
                <Image
                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${diptych.left.file_name}`}
                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${diptych.left.file_name}`}
                    alt={diptych.left.alt || `${category.category_name} banner image`}
                    title={category.category_name}
                    fill
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    priority={isFirstSlide}
                    fetchPriority={isFirstSlide ? 'high' : 'auto'}
                    quality={90}
                    sizes="(max-width: 767px) 0px, (max-width: 1800px) 40vw, 720px"
                    onLoad={() => setLeftLoaded(true)}
                />
            </div>
            <div className={`relative ${rightWidth}`}>
                {diptych.right.featured_products?.length > 0 && (
                    <FeaturedProductsPopup
                        products={diptych.right.featured_products}
                        position="bottom-right"
                    />
                )}
                <Image
                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${diptych.right.file_name}`}
                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${diptych.right.file_name}`}
                    alt={diptych.right.alt || `${category.category_name} collection image`}
                    title={`${category.category_name} collection`}
                    fill
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    priority={isFirstSlide}
                    fetchPriority={isFirstSlide ? 'high' : 'auto'}
                    quality={90}
                    sizes="(max-width: 767px) 0px, (max-width: 1800px) 60vw, 1080px"
                    onLoad={() => setRightLoaded(true)}
                />
            </div>
        </div>
    )
}

const CategoryDiptych = ({ category }) => {

    // Helper function to validate diptych has required images with file_name
    const validateDiptych = (diptychTest) => {
        return diptychTest &&
               diptychTest.left?.file_name &&
               diptychTest.right?.file_name
    }

    // Diptychs are always arrays - order comes from server (can be randomized server-side)
    const diptychsArray = category.diptychs || []
    const diptychs = diptychsArray.filter(validateDiptych)

    // Return null if no valid diptychs
    if (diptychs.length === 0) {
        return null
    }

    // Category metadata for overlays
    const isSupplierCategory = !!category.supplier?.supplier_id
    const isDesignerCategory = !!category.designer?.designer_id
    const isVendorCategory = isSupplierCategory || isDesignerCategory
    const vendorName = category.supplier?.supplier_name || category.designer?.designer_name
    const brandLogo = category.images?.brand_logo_white
    const brandLogoMobile = category.images?.brand_logo_left
    const bio = category.bio

    const categoryTree = category.nav?.tree || category.tree || []
    const isChildBrandPage = isVendorCategory && categoryTree.length > 2

    // Use first diptych's proportions for the overlay positioning
    const firstDiptych = diptychs[0]
    const leftWidth = firstDiptych.left.proportion === 'three_fifths' ? 'w-3/5' : 'w-2/5'
    const rightWidth = firstDiptych.left.proportion === 'three_fifths' ? 'w-2/5' : 'w-3/5'

    // Filter to diptychs that have mobile assets
    const mobileDiptychs = diptychs.filter(d => d.mobile_left?.file_name && d.mobile_right?.file_name)
    const hasMobileAssets = mobileDiptychs.length > 0

    // Pick random mobile diptych client-side to avoid hydration mismatch
    const [mobileDiptych, setMobileDiptych] = useState(null)
    useEffect(() => {
        if (hasMobileAssets) {
            setMobileDiptych(mobileDiptychs[Math.floor(Math.random() * mobileDiptychs.length)])
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {/* Mobile diptych - only if mobile assets exist */}
            {hasMobileAssets && (
                <div className="flex md:hidden">
                    {mobileDiptych ? (
                        <>
                            <div className={leftWidth}>
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${mobileDiptych.mobile_left.file_name}`}
                                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${mobileDiptych.mobile_left.file_name}`}
                                    alt={mobileDiptych.mobile_left.alt || `${category.category_name} banner image`}
                                    width={mobileDiptych.mobile_left.width || 600}
                                    height={mobileDiptych.mobile_left.height || 800}
                                    className="w-full h-auto"
                                    priority
                                    quality={90}
                                />
                            </div>
                            <div className={rightWidth}>
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${mobileDiptych.mobile_right.file_name}`}
                                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${mobileDiptych.mobile_right.file_name}`}
                                    alt={mobileDiptych.mobile_right.alt || `${category.category_name} collection image`}
                                    width={mobileDiptych.mobile_right.width || 900}
                                    height={mobileDiptych.mobile_right.height || 800}
                                    className="w-full h-auto"
                                    priority
                                    quality={90}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="w-full aspect-[33/29] bg-gray-2" />
                    )}
                </div>
            )}

            {/* Desktop diptych - all sizes if no mobile assets, lg+ only if mobile assets exist */}
            <div
                role="banner"
                aria-label={`${category.category_name} category banner`}
                className={`relative w-full aspect-[50/9] ${hasMobileAssets ? 'hidden md:block' : ''}`}
            >
                {/* Single random diptych - picked once on mount, no flash */}
                <SingleRandomDiptych diptychs={diptychs} category={category} />

                {/* Overlay: darken left side for text visibility */}
                <div className={`absolute top-0 left-0 bottom-0 ${leftWidth} bg-black/[15%] mix-blend-soft-light z-[5] pointer-events-none hidden lg:block`} />

                {/* Overlay: breadcrumbs and brand info (desktop) */}
                <div className={`absolute top-0 left-0 bottom-0 ${leftWidth} flex flex-col justify-between z-10 pointer-events-none`}>
                    {category.breadcrumbs && (
                        <div className="pointer-events-auto">
                            <Breadcrumbs breadcrumbs={category.breadcrumbs} on_white_background={false} className="ps-[var(--site-padding)] pt-2 hidden lg:block drop-shadow-lg" />
                        </div>
                    )}
                    <div className="hidden lg:block ps-[var(--site-padding)] pb-5 pr-10 pointer-events-auto">
                        {category.active_sale ? (
                            <>
                                {brandLogo?.file_name && (
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogo.file_name}`}
                                        overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogo.file_name}`}
                                        alt={brandLogo.alt || `${vendorName} logo` || `${category.category_name} brand logo`}
                                        title={vendorName || category.category_name}
                                        width={brandLogo.width}
                                        height={brandLogo.height}
                                        quality={90}
                                        sizes="200px"
                                        className="mb-5 max-w-[200px] drop-shadow-lg"
                                    />
                                )}
                                {category.active_sale.banner_line_1 && (
                                    <div className="text-white text-sm font-medium mb-0.5" dangerouslySetInnerHTML={{ __html: category.active_sale.banner_line_1 }} />
                                )}
                                {category.active_sale.banner_line_2 && (
                                    <div className="text-white text-sm mb-0.5">{category.active_sale.banner_line_2}</div>
                                )}
                                {category.active_sale.banner_line_3 && (
                                    <div className="text-white text-sm mb-0.5">{category.active_sale.banner_line_3}</div>
                                )}
                                {category.active_sale.banner_line_4 && (
                                    <div className="text-white text-sm">{category.active_sale.banner_line_4}</div>
                                )}
                            </>
                        ) : isVendorCategory && brandLogo?.file_name ? (
                            <>
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogo.file_name}`}
                                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogo.file_name}`}
                                    alt={brandLogo.alt || `${vendorName} logo` || `${category.category_name} brand logo`}
                                    title={vendorName || category.category_name}
                                    width={brandLogo.width}
                                    height={brandLogo.height}
                                    quality={90}
                                    sizes="200px"
                                    className="mb-3 pb-3 border-b-2 border-primary max-w-[200px] drop-shadow-lg"
                                />
                                <div className="text-white text-xs drop-shadow-md">{bio}</div>
                            </>
                        ) : isVendorCategory ? (
                            <>
                                <div className="flex items-center mb-2">
                                    <FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-xl mr-1 drop-shadow-lg" />
                                    <div className="text-white font-normal drop-shadow-lg">
                                        {category.display_name || vendorName || category.category_name}
                                    </div>
                                </div>
                                <div className="text-white text-xs drop-shadow-md">{bio}</div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center mb-2">
                                    <FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-xl mr-1 drop-shadow-lg" />
                                    <div className="text-white font-normal drop-shadow-lg">
                                        {category.display_name || category.category_name}
                                    </div>
                                </div>
                                <div className="text-white text-xs drop-shadow-md">{category.hover_text}</div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile content below diptych */}
            <div className={`site-padding pt-2 lg:hidden ${isChildBrandPage ? 'pb-0' : ''}`}>
                <Breadcrumbs breadcrumbs={category.breadcrumbs} on_white_background={true} showSchema={false} className={isChildBrandPage ? 'mb-0' : 'mb-2'} />
                <div>
                    {category.active_sale ? (
                        <div className="mb-4">
                            {brandLogoMobile?.file_name && (
                                <Image
                                    loading="lazy"
                                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogoMobile.file_name}`}
                                    overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogoMobile.file_name}`}
                                    alt={brandLogoMobile.alt || `${vendorName} logo` || `${category.category_name} brand logo`}
                                    title={vendorName || category.category_name}
                                    width={brandLogoMobile.width}
                                    height={brandLogoMobile.height}
                                    sizes="500px"
                                    quality={90}
                                    className="mb-4 max-w-[250px]"
                                />
                            )}
                            {category.active_sale.banner_line_1 && (
                                <div className="text-dark text-sm font-medium mb-0.5" dangerouslySetInnerHTML={{ __html: category.active_sale.banner_line_1 }} />
                            )}
                            {category.active_sale.banner_line_2 && (
                                <div className="text-dark text-sm mb-0.5">{category.active_sale.banner_line_2}</div>
                            )}
                            {category.active_sale.banner_line_3 && (
                                <div className="text-dark text-sm mb-0.5">{category.active_sale.banner_line_3}</div>
                            )}
                            {category.active_sale.banner_line_4 && (
                                <div className="text-dark text-sm">{category.active_sale.banner_line_4}</div>
                            )}
                        </div>
                    ) : isVendorCategory && !isChildBrandPage && brandLogoMobile?.file_name ? (
                        <>
                            <Image
                                loading="lazy"
                                src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogoMobile.file_name}`}
                                overrideSrc={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${brandLogoMobile.file_name}`}
                                alt={brandLogoMobile.alt || `${vendorName} logo` || `${category.category_name} brand logo`}
                                title={vendorName || category.category_name}
                                width={Math.round(brandLogoMobile.width / 2)}
                                height={Math.round(brandLogoMobile.height / 2)}
                                quality={90}
                                sizes="100px"
                                className="mb-4"
                            />
                            <p className="text-base leading-6 mb-2 text-justify first-letter:text-[2rem] first-letter:text-primary first-letter:uppercase">{bio}</p>
                        </>
                    ) : isVendorCategory && !isChildBrandPage ? (
                        <p className="text-base leading-6 mb-2 text-justify first-letter:text-[2rem] first-letter:text-primary first-letter:uppercase">{bio}</p>
                    ) : isChildBrandPage ? (
                        null
                    ) : (
                        <p className="text-base leading-6 mb-2 text-justify first-letter:text-[2rem] first-letter:text-primary first-letter:uppercase">{category.hover_text}</p>
                    )}
                </div>
            </div>
        </>
    )
}

export default CategoryDiptych
